import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "../components/Toast";
import LoadingSpinner from "../components/LoadingSpinner";
import ConversationCard from "../components/ConversationCard";
import EmptyState from "../components/EmptyState";
import useWidget from "../hooks/useWidget";
import { analyticsApi } from "../services/api";
import errorTracker from "../utils/errorTracker";
import { getErrorMessage, formatDateWithTime } from "../utils/formatters";
import { normalizeText } from "../utils/sanitize";
import { PAGINATION, STORAGE_KEYS } from "../config/constants";
import "./ConversationsViewer.css";

const ConversationsViewer = () => {
  const { id: widgetId, conversationId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Use the custom hook for widget loading
  const { widget, loading: widgetLoading } = useWidget(widgetId);

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || "",
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [showClosed, setShowClosed] = useState(false);

  const ITEMS_PER_PAGE = PAGINATION.CONVERSATIONS_PER_PAGE;

  const loadConversations = useCallback(
    async (signal, page = 1) => {
      setLoading(true);
      try {
        const response = await analyticsApi.getConversations(
          widgetId,
          page,
          ITEMS_PER_PAGE,
          { signal },
        );
        const responseTotalPages = response.pagination?.totalPages || 1;
        setConversations(response.conversations || []);
        setTotalPages(responseTotalPages);
        // Use the page parameter instead of currentPage state to avoid stale closure
        setHasMore(page < responseTotalPages);
      } catch (err) {
        if (err.name === "AbortError" || err.name === "CanceledError") {
          return;
        }
        errorTracker.captureException(err, {
          context: "loadConversations",
          widgetId,
        });
        toast.error(getErrorMessage(err, "Failed to load conversations"));
      } finally {
        setLoading(false);
      }
    },
    // INTENTIONAL: toast is a stable reference from useToast() context, omitting from deps
    // is safe and prevents unnecessary re-renders. See: https://reactjs.org/docs/hooks-faq.html#is-it-safe-to-omit-functions-from-the-list-of-dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widgetId],
  );

  const loadConversationMessages = useCallback(
    async (signal, convId) => {
      setLoadingMessages(true);
      try {
        const response = await analyticsApi.getConversationMessages(
          widgetId,
          convId,
          {
            signal,
          },
        );
        setMessages(response.messages || []);
        setSelectedConversation(
          conversations.find((c) => c.id === convId) ||
            response.conversation || { id: convId },
        );
      } catch (err) {
        if (err.name === "AbortError" || err.name === "CanceledError") {
          return;
        }
        errorTracker.captureException(err, {
          context: "loadConversationMessages",
          widgetId,
          conversationId: convId,
        });
        toast.error(
          getErrorMessage(err, "Failed to load conversation messages"),
        );
      } finally {
        setLoadingMessages(false);
      }
    },
    // INTENTIONAL: toast is a stable reference from useToast() context
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widgetId, conversations],
  );

  useEffect(() => {
    if (!widgetId) return;

    const controller = new AbortController();
    const signal = controller.signal;

    loadConversations(signal, currentPage);

    return () => {
      controller.abort();
    };
  }, [widgetId, currentPage, loadConversations]);

  useEffect(() => {
    if (conversationId) {
      const controller = new AbortController();
      loadConversationMessages(controller.signal, conversationId);

      return () => {
        controller.abort();
      };
    }
  }, [conversationId, loadConversationMessages]);

  const handleViewConversation = (conversation) => {
    setLoadingMessages(true); // Show loading immediately for better UX
    navigate(`/widgets/${widgetId}/conversations/${conversation.id}`);
  };

  const handleBackToList = () => {
    navigate(`/widgets/${widgetId}/conversations`);
    setSelectedConversation(null);
    setMessages([]);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ search: searchQuery });

      // Show warning about client-side search limitation (once per session)
      const warningShown = sessionStorage.getItem(
        STORAGE_KEYS.SEARCH_WARNING_SHOWN,
      );
      if (!warningShown) {
        toast.warning(
          "Note: Search is limited to the current page of results. Navigate to other pages to search more conversations.",
        );
        sessionStorage.setItem(STORAGE_KEYS.SEARCH_WARNING_SHOWN, "true");
      }
    } else {
      setSearchParams({});
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleStatusChange = async (conversation, newStatus) => {
    try {
      await analyticsApi.updateConversationStatus(widgetId, conversation.id, newStatus);
      setConversations((prev) =>
        prev.map((c) => (c.id === conversation.id ? { ...c, status: newStatus } : c)),
      );
      toast.success(
        newStatus === "closed" ? "Conversation closed" : "Conversation reopened",
      );
    } catch (err) {
      errorTracker.captureException(err, {
        context: "handleStatusChange",
        widgetId,
        conversationId: conversation.id,
      });
      toast.error(getErrorMessage(err, "Failed to update conversation status"));
    }
  };

  const filteredConversations = conversations.filter((conv) => {
    const isClosed = conv.status === "closed";
    if (!showClosed && isClosed) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      conv.id?.toString().includes(query) ||
      conv.session_id?.toLowerCase().includes(query) ||
      conv.last_message?.toLowerCase().includes(query)
    );
  });

  if (widgetLoading || (loading && conversations.length === 0)) {
    return (
      <div className="dashboard-home">
        <LoadingSpinner />
      </div>
    );
  }

  // If viewing a specific conversation
  if (conversationId && selectedConversation) {
    return (
      <div className="conversation-viewer">
        <div className="conversation-viewer-header">
          <button onClick={handleBackToList} className="btn btn-secondary">
            ← Back to Conversations
          </button>
          <div className="conversation-meta">
            <h2>Conversation #{conversationId.toString().slice(-6)}</h2>
            <p>
              {selectedConversation.started_at
                ? formatDateWithTime(selectedConversation.started_at)
                : "Unknown date"}
            </p>
          </div>
        </div>

        {loadingMessages ? (
          <LoadingSpinner />
        ) : (
          <div className="messages-transcript">
            {messages.length > 0 ? (
              messages.map((message, index) => (
                <div
                  key={message.id || index}
                  className={`message-bubble message-${message.role || "user"}`}
                >
                  <div className="message-header">
                    <span className="message-role">
                      {message.role === "user" ? "👤 User" : "🤖 Assistant"}
                    </span>
                    <span className="message-time">
                      {message.created_at
                        ? formatDateWithTime(message.created_at)
                        : ""}
                    </span>
                  </div>
                  <div className="message-content">
                    {normalizeText(message.content || "")}
                  </div>
                  {message.tokens?.prompt || message.tokens?.completion ? (
                    <div className="message-tokens">
                      {message.tokens?.prompt > 0 && (
                        <span>
                          📥 {message.tokens.prompt.toLocaleString()} in
                        </span>
                      )}
                      {message.tokens?.completion > 0 && (
                        <span>
                          📤 {message.tokens.completion.toLocaleString()} out
                        </span>
                      )}
                    </div>
                  ) : null}
                </div>
              ))
            ) : (
              <EmptyState message="No messages in this conversation" />
            )}
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="conversations-viewer">
      <div className="conversations-header">
        <div className="conversations-header-content">
          <h1>Conversations</h1>
          <p className="subtitle">
            {widget
              ? `Conversations for "${widget.name}"`
              : "Widget Conversations"}
          </p>
        </div>
        <button
          onClick={() => navigate("/widgets")}
          className="btn btn-secondary"
        >
          Back to Widgets
        </button>
      </div>

      <div className="conversations-filters">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-group">
            <input
              type="text"
              placeholder="Search by ID, session, or message content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              aria-label="Search conversations"
            />
            {searchQuery && (
              <span className="search-hint">Searching current page only</span>
            )}
          </div>
          <button type="submit" className="btn btn-primary">
            Search
          </button>
        </form>
        <button
          type="button"
          className={`btn btn-toggle-closed${showClosed ? " active" : ""}`}
          onClick={() => setShowClosed((prev) => !prev)}
        >
          {showClosed ? "Hide Closed" : "Show Closed"}
        </button>
      </div>

      {filteredConversations.length > 0 ? (
        <>
          <div className="conversations-list">
            {filteredConversations.map((conversation) => (
              <ConversationCard
                key={conversation.id}
                conversation={conversation}
                onClick={handleViewConversation}
                onStatusChange={handleStatusChange}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn btn-secondary"
              >
                ← Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!hasMore && currentPage >= totalPages}
                className="btn btn-secondary"
              >
                Next →
              </button>
            </div>
          )}
        </>
      ) : (
        <EmptyState
          message="No conversations found"
          subtitle={
            searchQuery
              ? "Try adjusting your search query"
              : "Conversations will appear here once users start chatting with your widget"
          }
        />
      )}
    </div>
  );
};

export default ConversationsViewer;
